import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Button, Icon, Spinner } from '@iq/react-components';

import {
  downloadComponentsExcel,
  setImportErrors,
  getImportErrors,
  getComponentsImportedAt,
  resetImportedComponentsAt,
  upsertComponents,
  getStaticComponents,
} from '../../../../../../../bundles/components';
import { requestModels, getAllModels } from '../../../../../../../bundles/models';
import {
  createGeometriesAndMappings,
  getMappingsImported,
  resetImportedMappingsAt,
  getImportErrors as getMappingsImportErrors,
  setImportErrors as setMappingsImportErrors,
} from '../../../../../../../bundles/geometry-mappings';
import { parseAutomationExcel } from '../../../../../../../externalUtils';
import Heading from '../../../../../../Heading';
import ConfirmationDialog from '../../../../../../ConfirmationDialog';
import InstructionalModal from '../../../../../../InstructionalModal';
import { displayNotification, getNotification } from '../../../../../../../bundles/notifications';

const addModelToComponents = (components, documents) => {
  const updatedComponents = [...components];

  for (const document of documents) {
    const componentIndex = components.findIndex(
      (c) => c.itemDesignation === document.itemDesignation
    );
    if (componentIndex !== -1) {
      updatedComponents[componentIndex] = {
        ...updatedComponents[componentIndex],
        models: [{ modelId: document.modelId, customName: document.displayName }],
      };
    }
  }

  return updatedComponents;
};

const ImportStep = ({ site, onNext }) => {
  const dispatch = useDispatch();
  const currentComponents = useSelector(getStaticComponents);
  const compsImported = useSelector(getComponentsImportedAt);
  const mappingsImported = useSelector(getMappingsImported);
  const importErrors = useSelector(getImportErrors);
  const mappingImportErrors = useSelector(getMappingsImportErrors);
  const currentSiteModels = useSelector(getAllModels);
  const fullScreenReference = useRef(null);

  const [importing, setImporting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);

  async function uploadDataFromExcel(file) {
    const { org, id: siteId } = site;

    const drawings = Object.values(currentSiteModels).map((model) => {
      if (model.versions.length === 0) {
        return {
          modelId: model.id,
          versionId: '',
          originalName: '',
          displayName: model.name,
        };
      }
      return {
        modelId: model.id,
        versionId: model.versions.at(-1).id,
        originalName: model.versions.at(-1).originalName,
        displayName: model.name,
      };
    });

    try {
      const [parsedComponentsWithAttributes, documentsWithShapes, parsingErrors] =
        await parseAutomationExcel({
          file,
          drawings,
          currentComponents,
        });

      if (parsingErrors.length > 0) {
        dispatch(setImportErrors(parsingErrors));
      } else {
        const documentsWithShapesWithModels = addModelToComponents(
          parsedComponentsWithAttributes,
          documentsWithShapes
        );

        dispatch(upsertComponents(siteId, org, documentsWithShapesWithModels));

        dispatch(createGeometriesAndMappings(siteId, documentsWithShapes));
      }
    } catch {
      dispatch(displayNotification(getNotification('uploadCompExcel', 'error')()));
    }
  }

  const onDropAccepted = ([file]) => {
    setImporting(true);
    dispatch(requestModels(site.id));
    uploadDataFromExcel(file);
  };

  const onDropRejected = () => {
    dispatch(setImportErrors(['Imports should be from a single Excel file.']));
  };

  const { getInputProps, getRootProps } = useDropzone({
    disabled: importing,
    multiple: false,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [] },
    onDropAccepted,
    onDropRejected,
  });

  useEffect(() => {
    if (importing && compsImported && mappingsImported) {
      setImporting(false);
      dispatch(resetImportedComponentsAt());
      dispatch(resetImportedMappingsAt());
      onNext();
    }
  }, [importing, compsImported, mappingsImported]);

  useEffect(() => {
    if (importErrors.length > 0) {
      setImporting(false);
      setShowErrors(true);
    } else {
      setShowErrors(false);
    }
  }, [importErrors]);

  useEffect(() => {
    if (mappingImportErrors.length > 0) {
      dispatch(setImportErrors(mappingImportErrors));
    }
  }, [mappingImportErrors]);

  return (
    <>
      <div className="component-import-step">
        <Heading
          contentLeft={<div className="title">Import components from an Excel</div>}
          large
        />
        <div className="component-import-step__button-row">
          <Button
            onClick={() => dispatch(downloadComponentsExcel(site))}
            activity="secondary"
            design="text"
            icon={
              <div className="icon-green">
                <Icon icon="he-docexcel" />
              </div>
            }
          >
            Export component template
          </Button>
          <div className="component-import-step__info-trigger">
            <Icon icon="he-help" />
            Excel info
          </div>
          <div className="component-import-step__info-tooltip">
            <p>The downloaded excel will contain all of the components for the site.</p>
            <p>When updating the excel you can edit or add new components.</p>
          </div>
        </div>
        <div
          className="component-import-step__drop-zone"
          {...getRootProps()}
        >
          <input
            data-testid="drop-zone-input"
            name="files"
            {...getInputProps()}
          />
          {importing ? (
            <Spinner />
          ) : (
            <div>
              <Icon
                icon="he-upload"
                size="m"
              />
              <p>{'Drag & drop components excel here, or click to select files'}</p>
            </div>
          )}
        </div>
        {/* <Button
          onClick={() => setShowInstruction(true)}
          activity="secondary"
          design="text"
          icon={
            <div className="icon-wiz-color">
              <Icon icon="he-info" />
            </div>
          }
        >
          How to prepare a component excel
        </Button> */}
        {showInstruction && (
          <InstructionalModal
            fullScreenRef={fullScreenReference}
            onClose={() => setShowInstruction(false)}
            identifier="wiz-comp-import"
            title="How to prepare a component excel"
          />
        )}
      </div>
      {showErrors ? (
        <ConfirmationDialog
          onConfirm={() => {
            dispatch(setMappingsImportErrors([]));
            dispatch(setImportErrors([]));
          }}
          onCancel={() => {
            dispatch(setMappingsImportErrors([]));
            dispatch(setImportErrors([]));
          }}
          showCancel={false}
          title="Import errors"
          subtitle="The following errors occurred while attempting import:"
          body={importErrors.map((error, index) => (
            <p key={`err-${index}`}>{error}</p>
          ))}
          className="setup-wizard-errors"
        />
      ) : null}
    </>
  );
};

export default ImportStep;
